import React from 'react'
import { useI18n } from '../../i18n'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import WorkflowProgressRail from './WorkflowProgressRail'
import ComplianceWorkflowSidebar, { type WorkflowSection } from './ComplianceWorkflowSidebar'
import StandardActivationWizard from './StandardActivationWizard'
import ScopeBuilderWorkspace from './ScopeBuilderWorkspace'
import ApplicabilityDiscoveryPanel from './ApplicabilityDiscoveryPanel'
import TopologyReadinessWorkspace from './TopologyReadinessWorkspace'
import CertificationReadinessWorkspace from './CertificationReadinessWorkspace'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import wfStyles from '@/styles/pages/compliance-workflow/ComplianceWorkflow.module.scss'
import {
  fetchWorkflow,
  activateWorkflow,
  saveWorkflowScope,
  finalizeWorkflowScope,
  fetchWorkflowApplicability,
  fetchWorkflowReadiness,
  fetchWorkflowBlockers,
  fetchWorkflowTopology,
  type ComplianceWorkflowPayload,
} from '../../services/complianceWorkflowApi'
import { confirmReuseCandidate, rejectReuseCandidate } from '../../services/complianceReuseApi'
import ReuseConfirmationQueue from '../compliance-reuse/ReuseConfirmationQueue'
import OperationalAssurancePanel from '../control-assurance/OperationalAssurancePanel'
import WorkflowOperationalEventsPanel from '../compliance-runtime/WorkflowOperationalEventsPanel'
import { featureFlags } from '../../config/featureFlags'
import { useComplianceExecution } from '../../context/ComplianceExecutionContext'
import { resumeExecutionSession } from '../../services/complianceExecutionApi'
import WorkflowResumeCard from '../compliance-execution/WorkflowResumeCard'
import ExecutionProgressStrip from '../compliance-execution/ExecutionProgressStrip'
import ExecutionTimeline from '../compliance-execution/ExecutionTimeline'
import ControlExecutionPanel from '../compliance-execution/ControlExecutionPanel'
import ScopeAuthorityDiagnosticsPanel from './ScopeAuthorityDiagnosticsPanel'

interface Props {
  standardCode: string
}

export default function ComplianceWorkflowWorkspace({ standardCode }: Props) {
  const { t } = useI18n()
  const execution = useComplianceExecution()
  const [workflow, setWorkflow] = React.useState<ComplianceWorkflowPayload | null>(null)
  const [section, setSection] = React.useState<WorkflowSection>('scope')
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const [organizationWide, setOrganizationWide] = React.useState(false)
  const [discoveries, setDiscoveries] = React.useState<unknown[] | null>(null)
  const [readiness, setReadiness] = React.useState<unknown>(null)
  const [blockers, setBlockers] = React.useState<unknown[]>([])
  const [drift, setDrift] = React.useState<unknown[]>([])
  const [topology, setTopology] = React.useState<{ graph?: { nodes: unknown[]; edges: unknown[] } } | null>(null)
  const [selectedControlKey, setSelectedControlKey] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchWorkflow(standardCode)
      setWorkflow(data)
      const scope = data.scopeDefinition as { informationSystemIds?: string[]; organizationWide?: boolean } | undefined
      const validSystemIds = new Set((data.informationSystems || []).map((s) => s.id))
      const restoredScopeIds = (scope?.informationSystemIds || []).filter((id) =>
        validSystemIds.has(id),
      )
      setSelectedIds(restoredScopeIds)
      setOrganizationWide(Boolean(scope?.organizationWide))
      const persisted = (data as { persistedDiscoveries?: unknown[] }).persistedDiscoveries
      const execCtx = (data as { executionContext?: { resume?: { discoveries?: unknown[] } } }).executionContext
      const restored =
        persisted ||
        execCtx?.resume?.discoveries ||
        (data as { executionContext?: { discoveries?: unknown[] } }).executionContext?.discoveries
      if (Array.isArray(restored) && restored.length > 0) {
        setDiscoveries(restored)
      }
      if (execution.enabled) {
        execution.setSelectedStandard(standardCode)
        await execution.refreshContext(standardCode)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load workflow')
    } finally {
      setLoading(false)
    }
  }, [standardCode, execution.enabled])

  React.useEffect(() => {
    void load()
  }, [load])

  const handleResumeSession = async () => {
    setBusy(true)
    try {
      await resumeExecutionSession(standardCode)
      await load()
      await execution.refreshContext(standardCode)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Resume failed')
    } finally {
      setBusy(false)
    }
  }

  const toggleSystem = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const handleActivate = async () => {
    setBusy(true)
    try {
      await activateWorkflow(standardCode)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Activation failed')
    } finally {
      setBusy(false)
    }
  }

  const handleSaveScope = async () => {
    setBusy(true)
    try {
      await saveWorkflowScope(standardCode, {
        informationSystemIds: selectedIds,
        organizationWide,
      })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  const handleFinalize = async () => {
    setBusy(true)
    setError(null)
    try {
      await saveWorkflowScope(standardCode, { informationSystemIds: selectedIds, organizationWide })
      await finalizeWorkflowScope(standardCode)
      await load()
      try {
        const r = await fetchWorkflowReadiness(standardCode)
        setReadiness(r)
      } catch (readinessErr) {
        setReadiness({
          materializedEnabled: true,
          systems: [],
          explanations: [
            {
              type: 'WARNING',
              message:
                readinessErr instanceof Error
                  ? readinessErr.message
                  : 'Scope finalized; readiness panel will update after recompute.',
            },
          ],
        })
      }
      setSection('readiness')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Finalize failed')
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmReuse = async (id: string) => {
    setBusy(true)
    try {
      await confirmReuseCandidate(id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Confirm reuse failed')
    } finally {
      setBusy(false)
    }
  }

  const handleRejectReuse = async (id: string) => {
    setBusy(true)
    try {
      await rejectReuseCandidate(id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject reuse failed')
    } finally {
      setBusy(false)
    }
  }

  const handleDiscover = async () => {
    setBusy(true)
    try {
      const d = await fetchWorkflowApplicability(standardCode)
      setDiscoveries(d.discoveries || [])
      await execution.refreshContext(standardCode)
      setSection('applicability')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Discovery failed')
    } finally {
      setBusy(false)
    }
  }

  const loadReadinessData = React.useCallback(async () => {
    if (!standardCode) return
    try {
      const [b, r] = await Promise.all([
        fetchWorkflowBlockers(standardCode),
        fetchWorkflowReadiness(standardCode),
      ])
      setBlockers(b.blockers || [])
      setReadiness(r)
      const dr = (r.systems || []).flatMap((s: { drift?: unknown[] }) => s.drift || [])
      setDrift(dr as unknown[])
    } catch (e) {
      setReadiness({
        materializedEnabled: false,
        message: e instanceof Error ? e.message : 'Readiness unavailable',
      })
    }
  }, [standardCode])

  React.useEffect(() => {
    if (!standardCode || !workflow) return
    void loadReadinessData()
  }, [standardCode, workflow?.workflowStage, loadReadinessData])

  React.useEffect(() => {
    if (section !== 'certification' || !standardCode) return
    void loadReadinessData()
  }, [section, standardCode, loadReadinessData])

  React.useEffect(() => {
    if (section !== 'topology' || !standardCode) return
    void fetchWorkflowTopology(standardCode).then(setTopology).catch(() => setTopology(null))
  }, [section, standardCode])

  if (loading) {
    return <p className={wfStyles.loadingText}>{t('common.loading', 'Loading…')}</p>
  }

  if (error) {
    return <p className={wfStyles.alertError} role="alert">{error}</p>
  }

  if (!workflow) return null

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={getStandardDisplayName(t, standardCode)}
        meta={workflow.workflowStage}
      >
        <WorkflowProgressRail steps={workflow.progressRail || []} onStepClick={() => {}} />
      </GovernanceListingSection>

      <StandardActivationWizard
        standardCode={standardCode}
        isActive={workflow.isActive}
        workflowStage={workflow.workflowStage}
        onActivate={handleActivate}
        loading={busy}
      />

      {featureFlags.COMPLIANCE_EXECUTION_ORCHESTRATION_V1 ? (
        <>
          <WorkflowResumeCard
            standardCode={standardCode}
            context={execution.context}
            onResume={handleResumeSession}
            busy={busy}
          />
          <ExecutionProgressStrip
            stage={execution.context?.activeStage || (workflow as { executionStage?: string }).executionStage}
            progress={execution.context?.progress}
            blockerCount={execution.context?.unresolvedBlockers?.length}
          />
          {execution.context?.lifecycleTimeline ? (
            <GovernanceListingSection title={t('complianceExecution.lifecycle', 'Execution lifecycle')}>
              <ExecutionTimeline steps={execution.context.lifecycleTimeline} />
              {execution.context.orchestrationNarratives?.map((line, i) => (
                <p key={i} className={wfStyles.itemMeta}>
                  {line}
                </p>
              ))}
            </GovernanceListingSection>
          ) : null}
        </>
      ) : null}

      {featureFlags.COMPLIANCE_REUSE_V1 && workflow.crossStandardReuseEnabled && (workflow.reuseQueue?.length || 0) > 0 ? (
        <ReuseConfirmationQueue
          candidates={workflow.reuseQueue as never}
          busy={busy}
          onConfirm={handleConfirmReuse}
          onReject={handleRejectReuse}
        />
      ) : null}

      {featureFlags.CONTROL_ASSURANCE_V1 && workflow.operationalAssurance ? (
        <OperationalAssurancePanel assurance={workflow.operationalAssurance} onRefresh={() => void load()} />
      ) : null}

      {featureFlags.COMPLIANCE_RUNTIME_V1 && workflow.operationalEvents ? (
        <WorkflowOperationalEventsPanel operationalEvents={workflow.operationalEvents} />
      ) : null}

      <div className={wfStyles.workspaceLayout}>
        <ComplianceWorkflowSidebar
          active={section}
          onSelect={setSection}
          workflowStage={workflow.workflowStage}
        />
        <div className={wfStyles.workspaceMain}>
          <ScopeAuthorityDiagnosticsPanel
            standardCode={standardCode}
            scopeAuthority={execution.context?.scopeAuthority}
          />
          {section === 'scope' && (
            <ScopeBuilderWorkspace
              workflow={{
                ...workflow,
                scopeAuthority: execution.context?.scopeAuthority || (workflow as { scopeAuthority?: unknown }).scopeAuthority,
              }}
              selectedIds={selectedIds}
              onToggleSystem={toggleSystem}
              organizationWide={organizationWide}
              onOrganizationWideChange={setOrganizationWide}
              onSave={handleSaveScope}
              onFinalize={handleFinalize}
              saving={busy}
            />
          )}
          {section === 'applicability' && (
            <>
              <ApplicabilityDiscoveryPanel
                discoveries={(discoveries || execution.context?.resume?.discoveries || execution.context?.discoveries) as never}
                onDiscover={handleDiscover}
                loading={busy}
                scopeRevision={execution.context?.scopeAuthority?.scopeRevision}
                staleDiscovery={
                  execution.context?.scopeAuthority?.staleDiscovery ||
                  (workflow as { shouldRerunDiscovery?: boolean }).shouldRerunDiscovery
                }
              />
              {featureFlags.COMPLIANCE_EXECUTION_ORCHESTRATION_V1 && selectedControlKey ? (
                <ControlExecutionPanel
                  standardCode={standardCode}
                  binding={
                    execution.context?.assessmentBindings?.find((b) => b.controlKey === selectedControlKey) ||
                    null
                  }
                  onClose={() => setSelectedControlKey(null)}
                />
              ) : null}
            </>
          )}
          {section === 'readiness' && (
            <TopologyReadinessWorkspace readiness={readiness as never} standardCode={standardCode} />
          )}
          {section === 'certification' && (
            <CertificationReadinessWorkspace blockers={blockers as never} drift={drift as never} />
          )}
          {section === 'topology' && topology?.graph ? (
            <GovernanceListingSection title={t('complianceWorkflow.scopeVisualization', 'Scope Visualization')}>
              <p className={wfStyles.itemMeta}>
                {topology.graph.nodes.length} nodes · {topology.graph.edges.length} edges
              </p>
              <div className={wfStyles.topologyPanel}>
                <ul className="m-0 p-0 list-none space-y-0.5">
                  {topology.graph.nodes.slice(0, 30).map((n: { id: string; label?: string; type?: string }) => (
                    <li key={n.id} className={wfStyles.itemMeta}>
                      {n.type}: {n.label || n.id}
                    </li>
                  ))}
                </ul>
              </div>
            </GovernanceListingSection>
          ) : null}
        </div>
      </div>
    </div>
  )
}
